import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Plus, Edit2, Trash2, AlertCircle, CheckCircle2, Search, Filter, XCircle, Lock, Eye, Copy } from 'lucide-react';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { useMessageTemplateStore } from '../../../core/services/MessageTemplateStore';
import { IMessageTemplate, CreateMessageTemplateDto } from '../../../core/services/MessageTemplateService';
import { useAuthStore } from '../../../core/services/AuthStore';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';

const serviceContainer = ServiceContainer.getInstance();
const templateService = serviceContainer.getMessageTemplateService();

export const TemplatesPage: React.FC = () => {
  const { user } = useAuthStore();
  const {
    templates,
    setTemplates,
    isLoading,
    setLoading,
    error,
    setError,
    clearError,
    addTemplate,
    updateTemplate,
    deleteTemplate,
  } = useMessageTemplateStore();

  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<IMessageTemplate | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<IMessageTemplate | null>(null);
  const [copiedTemplateId, setCopiedTemplateId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CreateMessageTemplateDto>({
    title: '',
    body: '',
  });

  const canManageTemplates = !!user?.roles?.some((role) => role === 'admin' || role === 'gestor');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const data = await templateService.getAllTemplates();
      setTemplates(data);
    } catch (err) {
      setError('Erro ao carregar templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [setTemplates, setLoading, setError, clearError]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({ title: '', body: '' });
  };

  const clearFilters = () => {
    setSearchTerm('');
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = !searchTerm ||
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.body.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canManageTemplates) {
      setError('Você não tem permissão para criar ou editar templates');
      return;
    }
    
    if (!formData.title.trim() || !formData.body.trim()) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    if (formData.body.length > 1600) {
      setError('O template não pode exceder 1600 caracteres');
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      if (editingTemplate) {
        const updated = await templateService.updateTemplate(editingTemplate.id, formData);
        updateTemplate(editingTemplate.id, updated);
        setSuccessMessage('Template atualizado com sucesso!');
      } else {
        const newTemplate = await templateService.createTemplate(formData);
        addTemplate(newTemplate);
        setSuccessMessage('Template criado com sucesso!');
      }
      handleCloseForm();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(editingTemplate ? 'Erro ao atualizar template' : 'Erro ao criar template');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = (template: IMessageTemplate) => {
    setTemplateToDelete(template);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete || deleteConfirmText !== 'DELETAR') return;

    if (!canManageTemplates) {
      setError('Você não tem permissão para excluir templates');
      return;
    }

    try {
      await templateService.deleteTemplate(templateToDelete.id);
      deleteTemplate(templateToDelete.id);
      setSuccessMessage('Template deletado com sucesso!');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      setDeleteConfirmText('');
    } catch (err) {
      setError('Erro ao deletar template');
      console.error(err);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteConfirmText('');
  };

  const handleCopyTemplateBody = async (template: IMessageTemplate) => {
    try {
      await navigator.clipboard.writeText(template.body);
      setCopiedTemplateId(template.id);
      setTimeout(() => setCopiedTemplateId(null), 1800);
    } catch (err) {
      console.error('Erro ao copiar template:', err);
      setError('Não foi possível copiar a mensagem do template');
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getChannelStatuses = (template: IMessageTemplate) => {
    if (template.channelStatuses && template.channelStatuses.length > 0) {
      return template.channelStatuses;
    }

    return [
      { channel: 'RCS', status: 'not_supported' },
      { channel: 'SMS', status: 'not_supported' },
      { channel: 'Facebook Messenger', status: 'not_supported' },
      { channel: 'WhatsApp user initiated', status: 'not_supported' },
      { channel: 'WhatsApp business initiated', status: 'not_supported' },
    ];
  };

  const getChipClass = (channel: string, status: string): string => {
    const normalizedStatus = status.toLowerCase();
    if (channel.toLowerCase() === 'whatsapp business initiated') {
      if (normalizedStatus === 'approved') {
        return 'bg-green-50 text-green-700 border-green-200';
      }
      if (normalizedStatus === 'rejected') {
        return 'bg-red-50 text-red-700 border-red-200';
      }
      return 'bg-blue-50 text-blue-700 border-blue-200';
    }
    return 'bg-green-50 text-green-700 border-green-200';
  };

  if (isLoading && !templates.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-8 w-8 text-blue-600" />
            Templates de Mensagem
          </h1>
          <p className="text-slate-600 mt-2">Crie e gerencie templates reutilizáveis</p>
        </div>
        {canManageTemplates && (
          <Button onClick={() => setShowForm(true)} className="h-11">
            <Plus className="h-5 w-5 mr-2" />
            Novo Template
          </Button>
        )}
      </div>

      {!canManageTemplates && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Seu perfil pode visualizar e utilizar templates, mas não pode criar, editar ou excluir.
        </div>
      )}

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearError}>✕</Button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            <span>{successMessage}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSuccessMessage(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {searchTerm && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
            Pesquisar
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              id="searchTerm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título ou conteúdo..."
              className="h-11 pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Cards de Templates */}
      <Card>
        <CardContent className="p-6">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">
                {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template disponível'}
              </h2>
              <p className="text-slate-600">
                {searchTerm ? 'Tente ajustar os filtros de busca' : 'Crie seu primeiro template de mensagem para começar'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <Card
                  key={template.id}
                  className={`transition-shadow border-2 ${
                    template.isDefault
                      ? 'bg-slate-100 border-slate-300'
                      : 'hover:shadow-lg border-slate-200'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {template.isDefault ? (
                          <Lock className="h-5 w-5 text-slate-600" />
                        ) : (
                          <FileText className="h-5 w-5 text-blue-600" />
                        )}
                        <h3 className="font-semibold text-lg">{template.title}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {template.isDefault && (
                          <Badge variant="outline" className="bg-slate-200 text-slate-700 border-slate-300">
                            <Lock className="h-3 w-3 mr-1" />
                            Padrão
                          </Badge>
                        )}
                        <Badge variant="secondary">{template.body.length}/{template.charLimit}</Badge>
                      </div>
                    </div>
                    <p className={`text-sm mb-4 line-clamp-3 ${template.isDefault ? 'text-slate-700' : 'text-slate-600'}`}>
                      {truncateText(template.body, 100)}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 text-blue-700 hover:text-blue-800"
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver mensagem completa
                    </Button>
                    <div className="mb-3 border rounded-md border-slate-200 px-2.5 py-2 bg-slate-50/60">
                      <p className="text-[11px] font-medium text-slate-500 mb-2">Canais suportados:</p>
                      <div className="flex flex-wrap gap-2">
                        {getChannelStatuses(template).map((item) => (
                          <Badge
                            key={item.channel}
                            variant="outline"
                            className={`text-[10px] border font-normal px-2 py-0.5 ${getChipClass(item.channel, item.status)}`}
                          >
                            {item.channel.toLowerCase() === 'whatsapp business initiated' ? (
                              <AlertCircle className="h-2.5 w-2.5 mr-1" />
                            ) : (
                              <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                            )}
                            {item.channel}: {item.status}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between items-center gap-2 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyTemplateBody(template)}
                        title="Copiar mensagem"
                        className="text-slate-600 hover:text-slate-800"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        {copiedTemplateId === template.id ? 'Copiado' : 'Copiar'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template)}
                        disabled={template.isDefault || !canManageTemplates}
                        title={
                          !canManageTemplates
                            ? 'Seu perfil não pode excluir templates'
                            : template.isDefault
                              ? 'Template padrão do sistema é bloqueado'
                              : 'Deletar template'
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                    {template.isDefault && (
                      <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Template padrão do sistema (bloqueado para edição e exclusão).
                      </p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm && canManageTemplates} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingTemplate ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Template
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-blue-600" />
                  Novo Template
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                Título *
              </Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nome do template"
                  className="h-11 pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="body" className="text-sm font-medium text-slate-700">
                Mensagem *
              </Label>
              <textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Digite o conteúdo do template..."
                rows={8}
                maxLength={1600}
                required
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {formData.body.length} / 1600 caracteres
                </p>
                {formData.body.length > 1600 && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Limite excedido!
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={handleCloseForm} className="h-11">
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting || formData.body.length > 1600} className="h-11">
                {submitting ? 'Salvando...' : editingTemplate ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização Completa */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewTemplate?.isDefault ? (
                <Lock className="h-5 w-5 text-slate-600" />
              ) : (
                <FileText className="h-5 w-5 text-blue-600" />
              )}
              {previewTemplate?.title}
            </DialogTitle>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                {previewTemplate.isDefault && (
                  <Badge variant="outline" className="bg-slate-200 text-slate-700 border-slate-300">
                    <Lock className="h-3 w-3 mr-1" />
                    Template padrão (bloqueado)
                  </Badge>
                )}
                <Badge variant="secondary">
                  {previewTemplate.body.length}/{previewTemplate.charLimit} caracteres
                </Badge>
              </div>

              <div className="border rounded-md bg-slate-50 p-4 max-h-[380px] overflow-y-auto">
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {previewTemplate.body}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleCopyTemplateBody(previewTemplate)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copiedTemplateId === previewTemplate.id ? 'Copiado' : 'Copiar mensagem'}
                </Button>
                <Button variant="secondary" onClick={() => setPreviewTemplate(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-xl">Deletar Template</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Esta ação é irreversível!
              </p>
              <p className="text-sm text-red-700">
                Você está prestes a deletar o template:{' '}
                <span className="font-semibold">{templateToDelete?.title}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-sm font-medium text-slate-700">
                Para confirmar, digite <span className="font-bold text-red-600">DELETAR</span>:
              </Label>
              <Input
                id="confirm-delete"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Digite DELETAR"
                className="h-11"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleteConfirmText !== 'DELETAR' || isLoading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Deletando...' : 'Deletar Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
